import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { productionInputSchema, type ProductionInput } from '@pirouette/shared';
import { useCreateProduction } from '@/data/productions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/productions/new')({
  component: NewProduction,
});

function NewProduction() {
  const navigate = useNavigate();
  const createProduction = useCreateProduction();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProductionInput>({
    resolver: zodResolver(productionInputSchema),
    mode: 'onBlur',
    defaultValues: { season: 'Spring 2027' },
  });

  async function onSubmit(values: ProductionInput) {
    try {
      const created = await createProduction.mutateAsync(values);
      toast({ title: 'Production created', description: created.name, variant: 'success' });
      await navigate({ to: '/admin/productions/$id', params: { id: created.id } });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      <header>
        <Link
          to="/admin/productions"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Productions
        </Link>
        <h1 className="mt-2">New production</h1>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        noValidate
        className="surface-elevated grid gap-5 p-6"
      >
        <div>
          <Label htmlFor="p-name">Name</Label>
          <Input
            id="p-name"
            placeholder="Spring Recital 2027"
            aria-invalid={!!errors.name}
            {...register('name')}
          />
          {errors.name && (
            <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
              {errors.name.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="p-season">Season</Label>
          <Input id="p-season" placeholder="Spring 2027" {...register('season')} />
        </div>
        <div>
          <Label htmlFor="p-venue">Venue name</Label>
          <Input id="p-venue" placeholder="(optional)" {...register('venue_name')} />
        </div>
        <div>
          <Label htmlFor="p-notes">Notes</Label>
          <textarea
            id="p-notes"
            rows={3}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 focus-visible:outline-none"
            {...register('notes')}
          />
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Create production'}
          </Button>
          <Link to="/admin/productions">
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
